import React, { useState, useEffect } from "react"
import { useQuery, useQueryClient } from "react-query"
import {
  fetchFrequentTrades,
  getFrequentTradesURL,
} from "../../services/main.service"
import Paginator from "../composites/paginator"

import {
  Badge,
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Grid,
  Button,
  IconButton,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Grid as CGrid,
  useToast,
} from "@chakra-ui/react"
import _ from "lodash"
import BlockedLoader from "../composites/blocked-loader"
import DateRangePicker from "../composites/date-range-picker"
import { AiOutlineSearch, AiOutlineDownload } from "react-icons/ai"
import { InputWrapper } from "../composites"
import { SelectSymbols } from "../composites/select-symbols"
import { formatDate } from "../../utils"
import moment from "moment"
import { app } from "../../config"
import { da } from "date-fns/locale"
import api, { getBaseURL } from "../../services/api"

// Function to fetch all frequent trades data for CSV export
export const fetchAllFrequentTradesForCSV = async (
  startDate: string,
  endDate: string,
  loginId?: number,
  symbol: string = "",
  mins: number = 0,
  startProfit?: number,
  endProfit?: number
) => {
  try {

    let queryString = `sort=DESC&csv_download=true&limit=50000`

    if (loginId) {
      queryString += `&Login=${loginId}`
    }

    if (symbol) {
      queryString += `&symbol=${symbol}`
    }

    if (startDate) {
      queryString += `&startDate=${startDate}`
    }

    if (endDate) {
      queryString += `&endDate=${endDate}`
    }

    if (mins) {
      queryString += `&mins=${mins}`
    }

    if (typeof startProfit !== 'undefined') {
      queryString += `&startProfit=${startProfit}`
    }

    if (typeof endProfit !== 'undefined') {
      queryString += `&endProfit=${endProfit}`
    }

    // Return the full response instead of just data.data to get access to all fields
    const response = await api.get(`/frequently?${queryString}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching CSV data:", error);
    throw error; // Re-throw to be handled by the calling function
  }
}

// Helper function to convert JSON data to CSV format
function convertJsonToCsv(jsonArray: Record<string, any>[]) {
  if (!jsonArray || jsonArray.length === 0) {
    return '';
  }
  
  try {
    // Extract headers from the first object in the array
    const headers = Object.keys(jsonArray[0]);
    
    // Create the CSV header row
    const csvHeader = headers.join(',');
    
    // Create the CSV data rows with optimized processing
    const csvRows = jsonArray.map(obj => {
      return headers
        .map(header => {
          const value = obj[header];
          

          if (value === null || value === undefined) {
            return '';
          }
          

          if ((header === 'Time' || header === 'StartTime' || header === 'EndTime') && value) {
            return `"${value}"`;
          }
          

          if (typeof value === 'string' && 
              (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes(' '))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          
          return String(value); // Ensure value is a string
        })
        .join(',');
    });
    

    return [csvHeader, ...csvRows].join('\n');
  } catch (error) {
    console.error("Error converting JSON to CSV:", error);
    throw new Error("Failed to convert data to CSV format");
  }
}

interface DownloadFileParams {
  data: string;
  fileName: string;
  fileType: string;
}

const downloadFile = ({ data, fileName, fileType }: DownloadFileParams) => {
  try {

    const blob = new Blob([data], { type: fileType });
    

    const a = document.createElement('a');
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(a.href); 
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw new Error("Failed to download file");
  }
}

const options = [
  { label: "All", value: undefined },
  { label: "Under 60 Secs", value: 60 },
  { label: "Under 5 mins", value: 300 },
  { label: "Under 10 mins", value: 600 },
  { label: "Under 15 mins", value: 900 },
  { label: "Under 30 mins", value: 1800 },
]

const FrequentTradesPage = () => {
  const [isSetForcedUpdate, setIsForcedUpdate] = useState(false)
  const [year, setyear] = useState("2023")
  const [currentPage, setCurrentpage] = useState(1)
  const [selectedTrade, setSelectedTrade] = useState<any>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const toast = useToast()

  const [[startDate, endDate], setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined])

  const [time, setTime] = useState<undefined | number>(undefined)

  const [loginId, setLoginId] = useState<number | undefined>(undefined)
  const [startProfit, setStartProfit] = useState<number | undefined>(-10000)
  const [endProfit, setEndProfit] = useState<number | undefined>(1000000000)
  const [symbolStr, setSymbolStr] = useState<string>("")
  const [finalState, setFinalState] = useState({
    loginId: loginId,
    symbolStr: symbolStr,
    startDate: startDate,
    endDate: endDate,
    startProfit: startProfit,
    endProfit: endProfit,
    time: time,
  })

  const [selectedPosition, setSelectedPosition] = useState<any>(null)

  const updateFinalState = () => {
    setFinalState({
      loginId,
      symbolStr,
      startDate,
      endDate,
      startProfit,
      endProfit,
      time,
    })
  }

  const {
    data,
    isLoading,
    isError,
    isRefetching,
    isFetched,
    isFetchedAfterMount,
  } = useQuery(
    [
      "deals",
      {
        year,
        currentPage,
        finalState,
      },
    ],
    () =>
      fetchFrequentTrades(
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr,
        currentPage,
        false,
        finalState.time,
        finalState?.startProfit,
        finalState?.endProfit
      ),
    {
      refetchInterval: 4000,
    }
  )

  useEffect(() => {
    if (isFetchedAfterMount) {
      setIsForcedUpdate(true)
    }
  }, [currentPage, year])

  useEffect(() => {
    if (!isFetchedAfterMount) {
      if (!isRefetching) {
        if (isSetForcedUpdate) {
          setIsForcedUpdate(false)
        }
      }
    }
  }, [isRefetching])

  const getPropertyKeyForCaption = (caption: string): string => {
    const mapping: Record<string, string> = {
      "PositionID": "PositionID",
      "LoginID": "Login",
      "Symbol": "Symbol",
      "Profit": "TotalProfitAndLoss",
      "StartTime": "EarliestEntryTime",
      "EndTime": "LatestEntryTime",
      "TimeDif": "TimeDifferenceInSeconds",
      "Show Trades": ""
    };
    return mapping[caption] || caption;
  };

  // Function to download CSV with all data
  const downloadCSV = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch all data for CSV with the current filter parameters
      const response = await fetchAllFrequentTradesForCSV(
        finalState?.startDate ? formatDate(finalState?.startDate) : "",
        finalState?.endDate
          ? formatDate(finalState.endDate)
          : finalState?.startDate
          ? formatDate(finalState?.startDate)
          : "",
        finalState?.loginId,
        finalState?.symbolStr,
        finalState.time,
        finalState?.startProfit,
        finalState?.endProfit
      );
      
      console.log("CSV response structure:", response);
      
 
      let dealsData;
      

      if (response?.data?.deals && Array.isArray(response.data.deals)) {
        dealsData = response.data.deals;
      } else if (response?.deals && Array.isArray(response.deals)) {
        dealsData = response.deals;
      } else if (response?.data?.deals?.data?.deals && Array.isArray(response.data.deals.data.deals)) {
        dealsData = response.data.deals.data.deals;
      } else {

        const findDealsArray = (obj: any): any[] | null => {
          if (!obj || typeof obj !== 'object') return null;
          
          // Check if this object has a deals property that's an array
          for (const key in obj) {
            if (key === 'deals' && Array.isArray(obj[key])) {
              return obj[key];
            }
          }
          
          // Recursively check nested objects
          for (const key in obj) {
            if (typeof obj[key] === 'object') {
              const result = findDealsArray(obj[key]);
              if (result) return result;
            }
          }
          
          return null;
        };
        
        dealsData = findDealsArray(response);
      }
      

      if (!dealsData || !Array.isArray(dealsData) || dealsData.length === 0) {
        toast({
          title: "No data available",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        setIsDownloading(false);
        return;
      }
      

      const transformedData = dealsData.map((deal: any) => {
        const dealObj: Record<string, string | number | null> = {};
        

        captions.forEach((caption) => {
          if (caption === "TimeDif" && deal.TimeDifferenceInSeconds) {
            dealObj[caption] = millisecondsToStr(parseInt(deal.TimeDifferenceInSeconds) * 1000);
          } else if (caption === "Show Trades") {
            
            dealObj[caption] = "";
          } else {
            const apiField = getPropertyKeyForCaption(caption);
            dealObj[caption] = apiField && deal[apiField] !== undefined ? deal[apiField] : "";
          }
        });
        
        return dealObj;
      });
      
      // Convert to CSV and download
      const csvData = convertJsonToCsv(transformedData);
      if (!csvData) {
        throw new Error("Failed to generate CSV data");
      }
      
      downloadFile({
        data: csvData,
        fileName: `frequent-trades-${formatDate(finalState.startDate || new Date())}.csv`,
        fileType: 'text/csv'
      });
      
      toast({
        title: "Download successful",
        description: `${dealsData.length} records downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Grid
        templateColumns={[
          "repeat(1, 1fr)",
          "repeat(2, 1fr)",
          "repeat(3, 1fr)",
          "repeat(4, 1fr)",
          "repeat(5, 1fr)",
        ]}
        gap={6}
      >
        <Box position="relative">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            setDateRange={setDateRange}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Login">
            <Input
              type="number"
              value={loginId}
              onChange={(e) => {
                setLoginId(
                  e?.target?.value ? parseInt(e?.target?.value) : undefined
                )
              }}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Start Profit">
            <Input
              type="number"
              value={startProfit}
              onChange={(e) => {
                setStartProfit(
                  e?.target?.value ? parseInt(e?.target?.value) : undefined
                )
              }}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="End Profit">
            <Input
              type="number"
              value={endProfit}
              onChange={(e) => {
                setEndProfit(
                  e?.target?.value ? parseInt(e?.target?.value) : undefined
                )
              }}
            />
          </InputWrapper>
        </Box>

        <Box my={4} width="100%">
          <SelectSymbols
            selectedSymbol={symbolStr ?? "ALL"}
            onOptionChange={(_option: string) => {
              setSymbolStr(_option)
            }}
          />
        </Box>

        <Box my={4} width="100%">
          <InputWrapper title="Time Difference">
            <Select
              value={time ? time : undefined}
              onChange={(e) => {
                console.log(e?.target?.value)
                if (e?.target?.value) {
                  setTime(parseInt(e?.target?.value))
                } else {
                  setTime(undefined)
                }
              }}
            >
              {options.map((_opt) => (
                <option value={_opt?.value} key={_opt?.label}> {_opt?.label}</option>
              ))}
            </Select>
          </InputWrapper>
        </Box>

        <Box>
          <Flex align="center">
            <Box pt={[0, "35px"]} pr={4}>
              <Button
                colorScheme="blue"
                onClick={() => {
                  setCurrentpage(1)
                  updateFinalState()
                }}
              >
                <Box as={AiOutlineSearch} mr={2} />
                Search
              </Button>
            </Box>
            <Box pt={[0, "35px"]}>
              <Button
                leftIcon={<AiOutlineDownload />}
                colorScheme="green"
                isDisabled={isLoading || isDownloading || !data?.deals?.data?.deals?.length}
                isLoading={isDownloading}
                loadingText="Downloading..."
                onClick={downloadCSV}
              >
                Download CSV
              </Button>
            </Box>
          </Flex>
        </Box>
      </Grid>

      <DealsGrid>
        {isLoading ? (
          <Tr>
            <Td colSpan={captions?.length}>
              <BlockedLoader isBlocked={isLoading}>
                <></>
              </BlockedLoader>
            </Td>
          </Tr>
        ) : (
          <>
            {data?.deals?.data?.deals?.length ? (
              <>
                {_.map(data?.deals?.data?.deals ?? [], (deal) => {
                  return (
                    <DealsTableRow
                      key={deal?.PositionID}
                      deal={deal}
                      setSelectedTrade={(val: any) => {
                        if (val?.PositionID) {
                          setSelectedPosition(parseInt(val?.PositionID))
                        }
                      }}
                    />
                  )
                })}
              </>
            ) : (
              <>
                <Tr>
                  <Td colSpan={captions?.length}>
                    <Box p={4} textAlign="center">
                      <Text color="text.regular">
                        No data found for the selected dates
                      </Text>
                    </Box>
                  </Td>
                </Tr>
              </>
            )}
          </>
        )}
      </DealsGrid>

      <Box my={4}>
        <Paginator
          currentPage={currentPage}
          totalPages={
            data?.deals?.data?.deals?.length ? currentPage + 1 : currentPage
          }
          onChangePage={(_p: number) => setCurrentpage(_p)}
        />
      </Box>

      {selectedPosition ? (
        <ViewDealsModal
          _dealsObj={selectedPosition ?? []}
          onClose={() => {
            setSelectedPosition(null)
          }}
        />
      ) : null}
    </>
  )
}

export default FrequentTradesPage

const captions2 = [
  "Deal",
  "Order",
  // "Position",
  "LoginID",
  // "Name",
  "Action",
  "Entry",
  "Symbol",
  "Status",
  //"Price SL",
  //"Price TP",
  //"Reason",
  "Profit",
  "Commission",
  //"Fee",
  //"Dealer",
  //"Comment",
  "StartTime",
  "EndTime",
  "TimeDif",
  "Show Trades",
  //"Profit",
]

const captions = [
  "PositionID",
  "LoginID",
  "Symbol",
  "Profit",
  "StartTime",
  "EndTime",
  "TimeDif",
  "Show Trades",
]

const DealsGrid = ({ children }: { children: React.ReactNode }) => {
  return (
    <>
      <Box overflowX="auto">
        <Table variant="striped" color="text.regular" border={1}>
          <Thead>
            <Tr my=".8rem" ps="0px">
              {captions.map((caption, idx) => {
                return (
                  <Th
                    color="gray.400"
                    key={idx}
                    ps={idx === 0 ? "0px" : "0px"}
                    textAlign={caption === "PL" ? "right" : "unset"}
                  >
                    {caption}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>{children}</Tbody>
        </Table>
      </Box>
    </>
  )
}

const DealsTableRow = ({
  deal,
  setSelectedTrade,
}: {
  deal: any
  setSelectedTrade: (_t: any) => void
}) => {
  return (
    <>
      <Tr>
        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.PositionID ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.Login ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.Symbol ?? "-"}
            </Text>
          </Flex>
        </Td>

        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.TotalProfitAndLoss ?? "-"}
            </Text>
          </Flex>
        </Td>

        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.EarliestEntryTime ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.LatestEntryTime ?? "-"}
            </Text>
          </Flex>
        </Td>
        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              {deal?.TimeDifferenceInSeconds
                ? millisecondsToStr(
                    parseInt(deal?.TimeDifferenceInSeconds) * 1000
                  )
                : "-"}
            </Text>
          </Flex>
        </Td>

        <Td pl={1} pr={2}>
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Text fontSize="md" fontWeight="bold" minWidth="100%">
              <Button colorScheme="blue" onClick={() => setSelectedTrade(deal)}>
                Show Trades
              </Button>
            </Text>
          </Flex>
        </Td>
      </Tr>
    </>
  )
}

function getTimeDifference(startDate: string, endDate: string) {
  const startMoment = moment(startDate, "YYYY-MM-DD HH:mm:ss")
  const endMoment = moment(endDate, "YYYY-MM-DD HH:mm:ss")

  const duration = moment.duration(endMoment.diff(startMoment))

  const years = duration.years()
  const months = duration.months()
  const days = duration.days()
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  let timeDifference = ""

  if (years > 0) {
    timeDifference += years + " year" + (years > 1 ? "s" : "") + " "
  }

  if (months > 0) {
    timeDifference += months + " month" + (months > 1 ? "s" : "") + " "
  }

  if (days > 0) {
    timeDifference += days + " day" + (days > 1 ? "s" : "") + " "
  }

  if (hours > 0) {
    timeDifference += hours + " hour" + (hours > 1 ? "s" : "") + " "
  }

  if (minutes > 0) {
    timeDifference += minutes + " minute" + (minutes > 1 ? "s" : "") + " "
  }

  if (seconds > 0) {
    timeDifference += seconds + " second" + (seconds > 1 ? "s" : "") + " "
  }

  if (timeDifference === "") {
    return "0 seconds"
  }

  return timeDifference.trim()
}

function millisecondsToStr(milliseconds: number) {
  // TIP: to find current time in milliseconds, use:
  // var  current_time_milliseconds = new Date().getTime();

  function numberEnding(_num: number) {
    return _num > 1 ? "s" : ""
  }

  var temp = Math.floor(milliseconds / 1000)
  var years = Math.floor(temp / 31536000)
  if (years) {
    return years + " year" + numberEnding(years)
  }
  //TODO: Months! Maybe weeks?
  var days = Math.floor((temp %= 31536000) / 86400)
  if (days) {
    return days + " day" + numberEnding(days)
  }
  var hours = Math.floor((temp %= 86400) / 3600)
  if (hours) {
    return hours + " hour" + numberEnding(hours)
  }
  var minutes = Math.floor((temp %= 3600) / 60)
  if (minutes) {
    return minutes + " minute" + numberEnding(minutes)
  }
  var seconds = temp % 60
  if (seconds) {
    return seconds + " second" + numberEnding(seconds)
  }
  return "less than a second" //'just now' //or other string you like;
}

const secondsToHumanReadableTime = (milliseconds: number) => {
  return moment.duration(milliseconds, "seconds").humanize()
}

const ViewDealsModal = ({
  onClose,
  _dealsObj,
}: {
  onClose: () => void
  _dealsObj: any
}) => {
  const [isFetchingTrades, setIsFetchingTrades] = useState(false)
  const [trades, setTrades] = useState<any[]>([])
  const toast = useToast()

  useEffect(() => {
    if (_dealsObj) {
      fetchTrades()
    } else {
      setIsFetchingTrades(false)
    }
  }, [_dealsObj])

  const fetchTrades = async () => {
    setIsFetchingTrades(true)

    try {
      const result = await api.get(`/position?positionID=${_dealsObj}`)
      setTrades(result?.data?.data?.deals?.data ?? [])
    } catch (error) {
      console.error("Error fetching trades:", error)
      toast({
        title: "Error fetching trades",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }

    setIsFetchingTrades(false)
  }

  const downloadModalCSV = () => {
    try {
      if (!trades || trades.length === 0) {
        toast({
          title: "No data available",
          status: "info",
          duration: 3000,
          isClosable: true,
        })
        return;
      }
      

      const transformedData = trades.map((trade) => {
        const tradeObj: Record<string, string | number | null> = {};
        
        keys.forEach((key) => {
          tradeObj[key] = trade[key] !== undefined ? trade[key] : "";
        });
        
        return tradeObj;
      });
      
      // Convert to CSV and download
      const csvData = convertJsonToCsv(transformedData);
      if (!csvData) {
        throw new Error("Failed to generate CSV data");
      }
      
      downloadFile({
        data: csvData,
        fileName: `position-trades-${_dealsObj}.csv`,
        fileType: 'text/csv'
      });
      
      toast({
        title: "Download successful",
        description: `${trades.length} trades downloaded`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error downloading modal CSV:", error);
      toast({
        title: "Download failed",
        description: "There was an error downloading the CSV file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const dealsCaptions: any[] = [
    "Deal",
    "Order",
    "Position",
    "Login",
    "Time",
    "Type",
    "Entry",
    "Volume",
    "Price",
    "Market Ask",
    "Market Bid",
    "Reason",
    "Commission",
    "Fee",
    "Profit",
    "Dealer",
  ]

  const keys = [
    "Order",
    "Deal",
    "PositionID",
    "Login",
    "Time",
    "Entry",
    "Action",
    "Volume",
    "Price",
    "MarketAsk",
    "MarketBid",
    "Reason",
    "Commission",
    "Fee",
    "Profit",
    "Dealer",
  ]
  return (
    <>
      <Modal isOpen={true} onClose={onClose} isCentered size="7xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>View Trades</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box overflow="auto" height="100%" maxHeight="70vh">
              <Table variant="striped" color="text.regular" border={1}>
                <Thead>
                  <Tr my=".8rem" ps="0px">
                    {dealsCaptions.map((caption, idx) => {
                      return (
                        <Th
                          color="gray.400"
                          key={idx}
                          ps={idx === 0 ? "0px" : "0px"}
                          textAlign={caption === "PL" ? "right" : "unset"}
                        >
                          {caption}
                        </Th>
                      )
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {isFetchingTrades ? (
                    <Tr>
                      <Td colSpan={dealsCaptions?.length}>
                        <Box p={4} textAlign="center">
                          <Text color="text.regular">Fetching Trades</Text>
                        </Box>
                      </Td>
                    </Tr>
                  ) : (
                    <>
                      {_.isEmpty(trades) ? (
                        <Tr>
                          <Td colSpan={dealsCaptions?.length}>
                            <Box p={4} textAlign="center">
                              <Text color="text.regular">No deals found</Text>
                            </Box>
                          </Td>
                        </Tr>
                      ) : (
                        _.map(trades, (deal) => (
                          <Tr key={deal.Deal}>
                            {_.map(keys, (_k, idx) => (
                              <Td pl={1} pr={2} key={idx}>
                                <Flex
                                  align="center"
                                  py=".8rem"
                                  minWidth="100%"
                                  flexWrap="nowrap"
                                >
                                  <Text
                                    fontSize="md"
                                    fontWeight="bold"
                                    minWidth="100%"
                                  >
                                    {deal[_k]}
                                  </Text>
                                </Flex>
                              </Td>
                            ))}
                          </Tr>
                        ))
                      )}
                    </>
                  )}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button 
              leftIcon={<AiOutlineDownload />}
              colorScheme="green" 
              mr={3} 
              isDisabled={isFetchingTrades || _.isEmpty(trades)}
              onClick={downloadModalCSV}
            >
              Download CSV
            </Button>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}